import { ProductProvider } from '@shopify/hydrogen-react';
import { PageInfo, Product, ProductFilter } from '@shopify/hydrogen-react/storefront-api-types';
import React, { useEffect, useLayoutEffect, useState } from 'react';
import {
  useLocation,
  useNavigationType,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { useStoreActions, useStoreState } from '../../store';
import InlineActions from '../app/header/InlineActions';
import { useShowInlineActions } from '../app/header/useInlineActions';
import PageShell from '../app/PageShell';
import ProductItem from '../products/ProductCard';
import { ViewedProductsProvider } from '../products/ViewedProductContext';
import Filters, { SortData } from './Filters';
import CollectionPagination from './CollectionPagination';
import Spinner from '../../components/ui/Loader';
import { getCollectionProducts } from '../../utils/storefront/collections/actions';
import { getFilterFromURL, getSortFromURL } from '../../utils/storefront/filters';
import { flatResponseEdges } from '../../utils/storefront/flatResponseEdges';

const CollectionPage: React.FC = () => {
  const { collection } = useParams<{ collection: string }>();
  const setContext = useStoreActions(actions => actions.orgContext.setContext);
  const currentCollectionHandle = useStoreState(state => state.orgContext.collectionHandle);
  const perPageRaw = useStoreState(state => state.orgContext.perPage);
  const perPage = (() => {
    const n = perPageRaw ? parseInt(perPageRaw, 10) : NaN;
    return Number.isFinite(n) && n > 0 && n <= 50 ? n : 6;
  })();
  const showInlineActions = useShowInlineActions();
  const navigationType = useNavigationType();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const after = searchParams.get('after') || undefined;
  const before = searchParams.get('before') || undefined;
  const currentPage = (() => {
    if (!after && !before) {
      return 1;
    }
    const page = parseInt(searchParams.get('page') || '1', 10);
    return Number.isFinite(page) && page > 0 ? page : 1;
  })();

  const [collectionProducts, setCollectionProducts] = useState<Product[]>([]);
  const [collectionProductsLoaded, setCollectionProductsLoaded] = useState<boolean>(false);
  const [collectionPageInfo, setCollectionPageInfo] = useState<PageInfo>();
  const [filter, setFilter] = useState<ProductFilter[]>([]);
  const [filterLoaded, setFilterLoaded] = useState<boolean>(false);
  const [isFetching, setIsFetching] = useState<boolean>(false);

  useEffect(() => {
    if (!collection || collection === currentCollectionHandle) return;
    setContext({ collectionHandle: collection });
  }, [collection, currentCollectionHandle, setContext]);

  useLayoutEffect(() => {
    if (navigationType !== 'POP') {
      window.scrollTo(0, 0);
    }
  }, [navigationType]);

  const fetchPage = (handle: string, activeFilter: ProductFilter[] = [], cancelled?: () => boolean) => {
    const sort = getSortFromURL(location.search);

    setCollectionProductsLoaded(false);
    setIsFetching(true);

    getCollectionProducts(
      handle,
      { after: after ?? null, before: before ?? null },
      activeFilter,
      sort?.type,
      sort?.reverse,
      perPage
    )
      .then(r => {
        if (cancelled?.()) return;
        setCollectionProducts(flatResponseEdges(r?.edges || []));
        setCollectionPageInfo(r?.pageInfo);
        setCollectionProductsLoaded(true);
        setIsFetching(false);
      })
      .catch(() => {
        if (cancelled?.()) return;
        setCollectionProductsLoaded(true);
        setIsFetching(false);
      });
  };

  const handleFilter = (activeFilter: ProductFilter[], _sort?: SortData) => {
    if (!collection) {
      return;
    }

    setFilter(activeFilter);
    !filterLoaded && setFilterLoaded(true);
  };

  const setPageCursor = (direction: 'next' | 'prev') => {
    if (!collectionPageInfo) {
      return;
    }

    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.delete('after');
      next.delete('before');

      const page = Math.max(1, parseInt(prev.get('page') || '1', 10) || 1);

      if (direction === 'next' && collectionPageInfo.endCursor) {
        next.set('after', collectionPageInfo.endCursor);
        next.set('page', String(page + 1));
      }

      if (direction === 'prev' && collectionPageInfo.startCursor) {
        const previousPage = Math.max(1, page - 1);
        next.set('before', collectionPageInfo.startCursor);
        if (previousPage === 1) {
          next.delete('page');
        } else {
          next.set('page', String(previousPage));
        }
      }

      return next;
    });
    window.scrollTo(0, 0);
  };

  useEffect(() => {
    if (!collection) {
      return;
    }

    let cancelled = false;
    const urlFilters = getFilterFromURL(location.search);
    setFilter(urlFilters);
    fetchPage(collection, urlFilters, () => cancelled);
    return () => {
      cancelled = true;
    };
  }, [collection, after, before, location.search]);

  // Keep the pagination mounted across fetches (collectionPageInfo is not cleared
  // during a load) so the controls don't collapse/reappear and flicker on page turns.
  const showPagination =
    collectionPageInfo &&
    (collectionPageInfo.hasPreviousPage || collectionPageInfo.hasNextPage || currentPage > 1);

  const paginationProps = {
    currentPage,
    hasPreviousPage: !!collectionPageInfo?.hasPreviousPage,
    hasNextPage: !!collectionPageInfo?.hasNextPage,
    isLoading: isFetching,
    onPrev: () => setPageCursor('prev'),
    onNext: () => setPageCursor('next'),
  };

  return (
    <PageShell className="collection">
      {showInlineActions && <InlineActions />}
      <div className="container-fluid">
        <div className="content-wrapper">
          <div className="page-inner">
            <div className="box">
              <div className="collection-content">
                {process.env.REACT_APP_FEATURE_FILTERS === 'true' && collection && (
                  <Filters
                    onFilterChange={(activeFilter: ProductFilter[], sort) => {
                      handleFilter(activeFilter, sort);
                    }}
                    collectionHandle={collection}
                  />
                )}
              </div>

              <div className="collection-body">
                {!collectionProductsLoaded && !collectionProducts.length && <Spinner />}

                {collectionProductsLoaded && !collectionProducts.length && !isFetching && (
                  <p>No results</p>
                )}

                {showPagination && <CollectionPagination variant="compact" {...paginationProps} />}

                <ViewedProductsProvider
                  innerWrapperClassName="collection-grid"
                  initialProducts={[]}
                >
                  {!!collectionProducts.length &&
                    collectionProducts.map((product, key) => (
                      <ProductProvider key={key} data={product}>
                        <ProductItem fullHeight={true} optionsShow={false} index={key} />
                      </ProductProvider>
                    ))}
                </ViewedProductsProvider>

                {showPagination && <CollectionPagination variant="compact" {...paginationProps} />}
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageShell>
  );
};

export default CollectionPage;
